// frontend/src/components/supplier/VendorSuppliersButton.jsx
//
// "Create from Shopify vendors" on the Supplier setup page: asks once, starts
// the background run that makes each vendor's supplier the default of that
// vendor's items, and reports how it ended on the app toast.

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';

import ConfirmDialog from '../common/ConfirmDialog';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { useVendorSuppliersStart, useVendorSuppliersStatus } from '../../hooks/useVendorSuppliers';
import { backgroundRunView } from '../../utils/backgroundRun';
import { translateRequestError } from '../../queries/api';
import { vendorSuppliersFailureMessage } from './vendorSuppliersFailureMessage';
import { showToast } from '../../components/common/appToastBus';

export default function VendorSuppliersButton() {
  const { t } = useTranslation();
  const { confirm, dialogProps } = useConfirmDialog();
  const startMutation = useVendorSuppliersStart();
  // The run this button started, so a poll answer about an earlier run is
  // never read as this one's result.
  const [run, setRun] = useState(null);
  const [startFailed, setStartFailed] = useState(false);
  const { state: polled, error: pollError } = useVendorSuppliersStatus({ enabled: run !== null });

  const view = backgroundRunView({
    run, polled, startFailed, pollFailed: !!pollError,
  });

  // Each run ends in one message, said once. A poll that cannot be read is
  // said once too, and does not end the run: polling goes on, and the run's
  // own end is still reported when a later poll reads it.
  const reportedRunRef = useRef(null);
  const warnedRunRef = useRef(null);
  useEffect(() => {
    if (run === null || reportedRunRef.current === run.id) return;
    if (view.succeeded) {
      reportedRunRef.current = run.id;
      showToast(t('supplierSetup.vendorSuppliers.done', {
        suppliers: view.summary.suppliers_created,
        items: view.summary.items_linked,
      }), 'success');
    } else if (view.failed) {
      reportedRunRef.current = run.id;
      showToast(vendorSuppliersFailureMessage(view.errorCode, t), 'error');
    } else if (view.statusUnavailable && warnedRunRef.current !== run.id) {
      warnedRunRef.current = run.id;
      showToast(t('supplierSetup.vendorSuppliers.statusUnavailable'), 'warning');
    }
  }, [run, view.succeeded, view.failed, view.statusUnavailable, view.summary,
    view.errorCode, t]);

  const handleClick = async () => {
    const ok = await confirm({
      title: t('supplierSetup.vendorSuppliers.confirmTitle'),
      message: t('supplierSetup.vendorSuppliers.confirmMessage'),
      confirmLabel: t('supplierSetup.vendorSuppliers.confirmButton'),
    });
    if (!ok) return;
    setStartFailed(false);
    setRun(null);
    try {
      const data = await startMutation.mutateAsync();
      setRun({ id: data.run_id, outcome: data.outcome });
      showToast(t('supplierSetup.vendorSuppliers.started'), 'info');
    } catch (err) {
      console.error('suppliers/from-vendors failed:', err.message);
      setStartFailed(true);
      showToast(translateRequestError(err, t, 'supplierSetup.vendorSuppliers.startFailed'), 'error');
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        // `view.pending` alone is also true before any run exists.
        loading={startMutation.isPending || (run !== null && view.pending)}
      >
        {t('supplierSetup.vendorSuppliers.button')}
      </Button>
      <ConfirmDialog {...dialogProps} />
    </>
  );
}
